(() => {
  const STORAGE_KEY = 'metrokenshin:wishlist:v2';
  const LEGACY_KEY = 'metrokenshin:wishlist:v1';
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const normalize = (item) => {
    if (!item || !item.handle) return null;
    return {
      handle: String(item.handle),
      title: item.title || '',
      url: item.url || `${root()}products/${item.handle}`,
      image: item.image || '',
      price: item.price || '',
      variantId: item.variantId ? String(item.variantId) : '',
      available: item.available !== false
    };
  };

  const readWishlist = () => {
    try {
      let raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        raw = localStorage.getItem(LEGACY_KEY);
        if (raw) localStorage.setItem(STORAGE_KEY, raw);
      }
      const saved = JSON.parse(raw || '[]');
      if (!Array.isArray(saved)) return [];
      const seen = new Set();
      return saved.map(normalize).filter((item) => {
        if (!item || seen.has(item.handle)) return false;
        seen.add(item.handle);
        return true;
      });
    } catch (_error) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    const clean = items.map(normalize).filter(Boolean);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(clean)); } catch (_error) {}
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: clean }));
  };

  const productFromButton = (button) => ({
    handle: button.dataset.productHandle,
    title: button.dataset.productTitle,
    url: button.dataset.productUrl,
    image: button.dataset.productImage,
    price: button.dataset.productPrice,
    variantId: button.dataset.productVariant,
    available: button.dataset.productAvailable === 'true'
  });

  const updateControls = (items = readWishlist()) => {
    const handles = new Set(items.map((item) => item.handle));

    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const active = handles.has(button.dataset.productHandle);
      const title = button.dataset.productTitle || 'this item';
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${title} ${active ? 'from' : 'to'} wishlist`);
      const label = button.querySelector('[data-wishlist-label]');
      if (label) label.textContent = active ? 'Saved to wishlist' : 'Add to wishlist';
    });

    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = items.length > 99 ? '99+' : items.length;
      count.hidden = items.length === 0;
    });

    document.querySelectorAll('[data-wishlist-link]').forEach((link) => {
      link.setAttribute('aria-label', `Wishlist with ${items.length} ${items.length === 1 ? 'item' : 'items'}`);
    });
  };

  const toggleProduct = (button) => {
    const product = productFromButton(button);
    if (!product.handle) return;
    const items = readWishlist();
    const index = items.findIndex((item) => item.handle === product.handle);
    if (index >= 0) items.splice(index, 1);
    else items.unshift(product);
    writeWishlist(items);
  };

  const makeElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  };

  const money = (cents) => {
    if (typeof cents !== 'number') return '';
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const fetchFreshProduct = async (saved) => {
    try {
      const response = await fetch(`${root()}products/${encodeURIComponent(saved.handle)}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Product unavailable');
      const product = await response.json();
      const savedVariant = product.variants.find((variant) => String(variant.id) === String(saved.variantId));
      const variant = (savedVariant && savedVariant.available ? savedVariant : null) || product.variants.find((item) => item.available) || product.variants[0];
      return {
        handle: product.handle,
        title: product.title,
        url: `${root()}products/${product.handle}`,
        image: product.featured_image || (product.images && product.images[0]) || saved.image,
        price: variant ? money(variant.price) : saved.price,
        variantId: variant ? String(variant.id) : '',
        available: Boolean(variant && variant.available),
        missing: false
      };
    } catch (_error) {
      return { ...saved, available: false, missing: true };
    }
  };

  const createWishlistCard = (product) => {
    const card = makeElement('article', 'product-card wishlist-card');

    const media = makeElement('a', 'product-card__media');
    media.href = product.url || `${root()}products/${product.handle}`;
    media.setAttribute('aria-label', `View ${product.title || 'saved artwork'}`);
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.title || 'Saved artwork';
      image.loading = 'lazy';
      media.append(image);
    } else {
      media.append(makeElement('div', 'product-card__placeholder', 'Artwork preview unavailable'));
    }
    card.append(media);

    const remove = makeElement('button', 'wishlist-button wishlist-card__remove');
    remove.type = 'button';
    remove.dataset.removeWishlist = product.handle;
    remove.setAttribute('aria-label', `Remove ${product.title || 'item'} from wishlist`);
    remove.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.7a5.5 5.5 0 0 0-7.8 0L12 5.8l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.4 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z"/></svg>';
    remove.setAttribute('aria-pressed', 'true');
    card.append(remove);

    const info = makeElement('div', 'product-card__info');
    const title = makeElement('h3', 'product-card__title');
    const titleLink = makeElement('a', '', product.title || 'Saved artwork');
    titleLink.href = product.url || `${root()}products/${product.handle}`;
    title.append(titleLink);
    info.append(title);

    const actions = makeElement('div', 'wishlist-card__actions');
    if (product.price) actions.append(makeElement('span', 'product-card__price', product.price));
    if (product.available && product.variantId) {
      const add = makeElement('button', 'wishlist-card__cart', 'Add to cart');
      add.type = 'button';
      add.dataset.wishlistAdd = product.variantId;
      actions.append(add);
    } else {
      actions.append(makeElement('span', 'product-card__badge-inline', product.missing ? 'Unavailable' : 'Sold out'));
    }
    info.append(actions);
    card.append(info);
    return card;
  };

  const renderWishlistPage = async (items = readWishlist()) => {
    const grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    const empty = document.querySelector('[data-wishlist-empty]');
    const status = document.querySelector('[data-wishlist-status]');

    if (!items.length) {
      grid.replaceChildren();
      if (empty) empty.hidden = false;
      if (status) status.textContent = '';
      return;
    }

    if (empty) empty.hidden = true;
    if (status) status.textContent = `Loading ${items.length} saved ${items.length === 1 ? 'print' : 'prints'}…`;
    grid.setAttribute('aria-busy', 'true');

    const freshItems = await Promise.all(items.map(fetchFreshProduct));
    grid.replaceChildren(...freshItems.map(createWishlistCard));
    grid.removeAttribute('aria-busy');
    if (status) status.textContent = `${freshItems.length} saved ${freshItems.length === 1 ? 'print' : 'prints'}`;

    const merged = freshItems.map(({ missing, ...item }) => item);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch (_error) {}
  };

  const addToCart = async (button) => {
    button.disabled = true;
    const original = button.textContent;
    button.textContent = 'Adding…';
    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.wishlistAdd), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Unable to add item');
      button.textContent = 'Added ✓';
      window.setTimeout(() => window.location.assign(`${root()}cart`), 300);
    } catch (_error) {
      button.textContent = 'Try again';
      button.disabled = false;
      window.setTimeout(() => { button.textContent = original; }, 1800);
    }
  };

  const syncSelectedVariant = (select) => {
    const layout = select.closest('.product-layout');
    if (!layout) return;
    const button = layout.querySelector('[data-wishlist-toggle]');
    if (!button) return;
    button.dataset.productVariant = select.value;
    const selected = select.options[select.selectedIndex];
    button.dataset.productAvailable = String(!selected.disabled);
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      toggleProduct(toggle);
      return;
    }

    const remove = event.target.closest('[data-remove-wishlist]');
    if (remove) {
      writeWishlist(readWishlist().filter((item) => item.handle !== remove.dataset.removeWishlist));
      return;
    }

    const add = event.target.closest('[data-wishlist-add]');
    if (add) addToCart(add);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('.product-form select[name="id"]')) syncSelectedVariant(event.target);
  });

  document.addEventListener('wishlist:change', (event) => {
    updateControls(event.detail);
    renderWishlistPage(event.detail);
  });

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY || event.key === LEGACY_KEY) {
      const items = readWishlist();
      updateControls(items);
      renderWishlistPage(items);
    }
  });

  const init = () => {
    document.querySelectorAll('.product-form select[name="id"]').forEach(syncSelectedVariant);
    const items = readWishlist();
    updateControls(items);
    renderWishlistPage(items);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
